<template>
  <div class="reportings-list">
    <b-alert v-model="hasError" variant="danger">
      {{ errorMessage }}
    </b-alert>

    <dx-data-grid
      :ref="dataGridFundingsRG"
      id="gridContainerReporting"
      :data-source="dataSource"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-min-width="50"
      columnResizingMode="widget"
      :show-borders="true"
      :showColumnLines="true"
      :showRowLines="true"
      key-expr="field_001"
      :no-data-text="$t('general.noEntries')"
      :column-auto-width="false"
      :word-wrap-enabled="true"
      :hover-state-enabled="true"
      @row-expanding="openRowExpanding"
      @contentReady="contentReady"
      :remote-operations="true"
      :sync-lookup-filter-values="false"
    >
      <dx-scrolling
        mode="virtual"
        row-rendering-mode="virtual"
      />
      <dx-paging :page-size="pageSize"/>
      <dx-filter-panel :visible="true"/>
      <dx-filter-row
        :visible="true"
        apply-filter=""
      />
      <dx-header-filter
        :visible="true"
      />
      <dx-column-chooser
        :enabled="true"
        mode="select"
      >
        <dx-column-chooser-search
          :enabled="true"
        />
      </dx-column-chooser>
      <dx-search-panel
        :visible="true"
        :width="320"
        placeholder="Search..."
      />
      <dx-column
        data-field="profile.cRM_Adress_id"
        data-type="number"
        :caption="$t('funding.list.id')"
        :width="100"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="field_001"
        data-type="number"
        :caption="$t('funding.list.fundingId')"
        :width="85"
        :visible="false"
        :allow-filtering="true"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.company"
        :caption="$t('funding.list.company')"
        :editor-options="{ showClearButton: true }"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="closingdate"
        :caption="stageFilter === 10 ? $t('funding.list.tradesale') : $t('funding.list.ipoclosingdate')"
        data-type="date"
        format="dd.MM.yyyy"
        :allow-editing="true"
        :editor-options="{ showClearButton: true }"
        :allow-header-filtering="false"
      />
      <dx-column
        v-if="stageFilter === 10"
        data-field="profile.acquiredBy"
        :caption="$t('funding.list.acquiredby')"
        :allow-header-filtering="false"
      />
      <dx-column
        v-if="stageFilter === 16"
        data-field="moneyinCHF"
        :caption="$t('funding.list.money')"
        :format="totalFormat"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.firstname"
        :caption="$t('funding.list.firstname')"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.lastname"
        :caption="$t('funding.list.lastname')"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.salutationId"
        :caption="$t('funding.list.salutationId')"
        :width="150"
        :visible="false"
        :allow-filtering="false"
      >
        <dx-lookup
          :data-source='salutationTypes'
          :display-expr='getDisplayExpr'
          value-expr='ansprechzeile_id'
        />
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.emailB1"
        :caption="$t('funding.list.emailB1')"
        :width="150"
        :visible="false"
        :allow-header-filtering="false"
      >
        <dx-email-rule/>
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.emailB2"
        :caption="$t('funding.list.emailB2')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      >
        <dx-email-rule/>
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.linkedInB"
        :caption="$t('funding.list.linkedInB')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.linkedInP"
        :caption="$t('funding.list.linkedInP')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.kantonId"
        :caption="$t('funding.list.canton')"
        :editor-options="{ showClearButton: true }"
        :width="150"
      >
        <dx-lookup
          :data-source="cantonTypes"
          display-expr="kantonEN"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="profile"
        data-type="number"
        :caption="$t('funding.list.roundSize')"
        :format="totalFormat"
        :editor-options="{ showClearButton: true }"
        :width="100"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="investmentsTypeId"
        data-type="number"
        :caption="$t('funding.list.type')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="investmentTypes"
          display-expr="investmentsType"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="investmentsStage"
        data-type="number"
        :caption="$t('funding.list.stage')"
        :editor-options="{ showClearButton: true }"
        :width="120"
        :visible="false"
      >
        <dx-lookup
          :data-source="investmentStages"
          display-expr="investmentsStage"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="statusId"
        data-type="number"
        :caption="$t('funding.list.status')"
        :editor-options="{ showClearButton: true }"
        :width="120"
        :visible="false"
      >
        <dx-lookup
          :data-source="profileStatus"
          display-expr="status"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        v-if="checkAdmin(currentUser.mitarbeiterId)"
        :caption="$t('funding.list.totalFunding')"
        :calculate-cell-value="calcTotal"
        data-field="profile.cRMFundingProfileV2s"
        :format="totalFormat"
        :visible="false"
        :allow-filtering="false"
        :allow-sorting="false"
        :width="120"
      />
      <dx-column
        data-field="profile.cRMAddress.technologyId"
        data-type="number"
        :caption="$t('funding.list.technology')"
        :editor-options="{ showClearButton: true }"
        :width="120"
        :visible="false"
      >
        <dx-lookup
          :data-source="brancheTypes"
          display-expr="brancheEn"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        :caption="$t('funding.list.sectors')"
        data-field="profile.pROFILProfilJOINCategories"
        cell-template="sectors-template"
        :calculate-cell-value="calculateSectors"
        :width="400"
        :visible="false"
        :editor-options="{ showClearButton: true }"
        :allow-sorting="false"
        :allow-filtering="false"
      >
        <dx-header-filter :data-source="sectorsHeaderFilter"/>
      </dx-column>
      <dx-column
        data-field="cRMFundingJOINProfilProfileV2s"
        cell-template="investors-template"
        :calculate-cell-value="calculateInvestors"
        :caption="$t('funding.list.investors')"
        :width="200"
        :visible="false"
        :editor-options="{ showClearButton: true }"
        :allow-sorting="false"
        :allow-filtering="false"
      >
        <dx-header-filter :data-source="investorsHeaderFilter"/>
      </dx-column>
      <dx-column
        data-field="privacyJoin"
        data-type="number"
        :caption="$t('funding.list.publicRound')"
        :editor-options="{ showClearButton: true }"
        :width="120"
        :visible="false"
      >
        <dx-lookup
          :data-source="publicRoundValues"
          display-expr="displayValue"
          value-expr="key"
          :allow-clearing="true"
        />
      </dx-column>
      <dx-column
        :caption="$t('funding.list.awards')"
        data-field="profile.cRMAddress.cONTESTWinners"
        cell-template="awards-template"
        :calculate-cell-value="calculateAwards"
        :width="400"
        :visible="false"
        :editor-options="{ showClearButton: true }"
        :allow-sorting="false"
        :allow-filtering="false"
      >
        <dx-header-filter :data-source="awardsHeaderFilter"/>
      </dx-column>
      <dx-toolbar>
        <dx-item
          location="before"
          template="totalGroupCount"
        />
        <dx-item
          name="searchPanel"
        />
        <dx-item
          location="after"
          template="actionmenu"
        />
      </dx-toolbar>
      <template #sectors-template="{ data }">
        <div v-if="data.data.profile && data.data.profile.pROFILProfilJOINCategories.length">
          <span
            v-for="category in data.data.profile.pROFILProfilJOINCategories"
            :key="category.kategorie_idId"
          >
            <span v-if="category.pROFILCategory">{{category.pROFILCategory.kategorieDE}}, </span>
            <span v-else></span>
          </span>
        </div>
        <div v-else>
          <span></span>
        </div>
      </template>
      <template #investors-template="{ data }">
        <div>
          <span
            v-for="investor in data.data.cRMFundingJOINProfilProfileV2s"
            :key="investor.field_001"
          >
            <span v-if="investor.profil_idId">{{investor.profile.cRMAddress.company}}<br></span>
          </span>
        </div>
      </template>
      <template #awards-template="{ data }">
        <div v-if="data.data.profile && data.data.profile.cRMAddress && data.data.profile.cRMAddress.cONTESTWinners">
          <span
            v-for="award in data.data.profile.cRMAddress.cONTESTWinners"
            :key="award.field_001"
          >
            <span v-if="award.cONTESTExecution && award.cONTESTExecution.cONTESTContest.category_idId === 1">{{award.cONTESTExecution.contest_release}}, </span>
            <span v-else></span>
          </span>
        </div>
        <div v-else>
          <span></span>
        </div>
      </template>
      <dx-master-detail
        :enabled="true"
        template="master-detail"
      />
      <template #master-detail="{ data }">
        <funding-master-detail v-if="data" :masterDetailData="data"/>
      </template>
      <template #totalGroupCount>
        <div class="informer" v-if="initTotalCount != 0">
          <span class="count">{{ totalDevExpress }} results out of {{ initTotalCount }} records</span>
        </div>
      </template>
      <template #actionmenu>
        <div>
          <v-menu
            offset-y
            bottom
            left
            content-class="toolbar-action-menu"
          >
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                color="secondary"
                v-bind="attrs"
                v-on="on"
              >
                <i class="fas fa-cog"></i> Actions
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                link
                @click="openColumnChooser"
              >
                <v-list-item-title><i class="fas fa-columns mr-1"></i> Column Chooser</v-list-item-title>
              </v-list-item>
              <v-list-item
                link
                @click="onExporting"
              >
                <v-list-item-title><i class="fas fa-file-excel mr-2"></i> Excel Export</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </dx-data-grid>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import FundingMasterDetail from '@/components/fundings/MasterDetail.vue'
  import { exportDataGrid } from 'devextreme/excel_exporter'
  import ExcelJS from 'exceljs'
  import saveAs from 'file-saver'
  import { createCustomStore } from '@/store/helpers/remoteOperation.js'
  import { errorHandler } from '@/helpers/formHelper.js'
  import moment from 'moment'
  import { isAdmin } from '@/helpers/auth.js'
  import {
    DxDataGrid,
    DxColumn,
    DxScrolling,
    DxSearchPanel,
    DxColumnChooser,
    DxColumnChooserSearch,
    DxHeaderFilter,
    DxFilterRow,
    DxFilterPanel,
    DxMasterDetail,
    DxLookup,
    DxEmailRule,
    DxPaging,
    DxToolbar,
    DxItem
  } from 'devextreme-vue/data-grid'

  const dataGridFundingsRG = 'dataGrid'

  export default {
    components: {
      DxDataGrid,
      DxColumn,
      DxScrolling,
      DxSearchPanel,
      DxColumnChooser,
      DxColumnChooserSearch,
      DxHeaderFilter,
      DxFilterRow,
      DxFilterPanel,
      DxMasterDetail,
      FundingMasterDetail,
      DxLookup,
      DxEmailRule,
      DxPaging,
      DxToolbar,
      DxItem
    },
    data () {
      return {
        hasError: false,
        errorMessage: '',
        loadingShow: true,
        dataIsLoaded: false,
        totalFormat: { style: 'currency', currency: 'CHF', useGrouping: true, minimumSignificantDigits: 3 },
        totalCount: 0,
        totalDevExpress: 0,
        initTotalCount: 0,
        dataGridFundingsRG,
        sectorsHeaderFilter: [],
        investorsHeaderFilter: [],
        awardsHeaderFilter: [],
        publicRoundValues: [{ key: '2', displayValue: '' }, { key: '1', displayValue: 'Yes' }],
        pageSize: 50,
        currentEditingRowData: null,
        currentDate: new Date()
      }
    },
    props: {
      rgFilter: {
        type: Number
      },
      stageFilter: {
        type: Number
      }
    },
    created () {
      this.getBasicValues('basicValues/GET_BRANCHE_TYPES', this.brancheTypes)
      this.getBasicValues('basicValues/GET_INVESTMENT_TYPES', this.investmentTypes)
      this.getBasicValues('basicValues/GET_INVESTMENT_STAGES', this.investmentStages)
      this.getBasicValues('basicValues/GET_PROFILE_STATUS', this.profileStatus)
      this.getBasicValues('basicValues/GET_CANTON_TYPES', this.cantonTypes)
      this.getBasicValues('basicValues/GET_SALUTATION_TYPES', this.salutationTypes)
      this.initStore()
    },
    mounted () {
      this.defaultState = JSON.stringify(this.dataGrid.state())
    },
    methods: {
      initStore () {
        const key = 'field_001'
        this.dataSource = createCustomStore({ key, self: this, methodName: 'getFundings', root: this.$root })
      },
      contentReady () {
        this.totalDevExpress = this.dataGrid.totalCount()
        if (this.initTotalCount === 0) {
          this.initTotalCount = this.dataGrid.totalCount()
        }
        // exporting is only enabled if resuts are less than 8'000, because of performance reasons
        /* const excelExporter = document.getElementsByClassName('dx-datagrid-export-button')
        if (this.totalDevExpress <= 8000) {
          excelExporter[0].classList.remove('disable-exporting')
        } else {
          excelExporter[0].classList.add('disable-exporting')
        } */
      },
      async getFundings (gqlOptions) {
        this.hasError = false
        this.$Progress.start()
        const gqlOptionsAndFilter = {
          gqlOptions,
          rgId: this.rgFilter,
          stageFilter: this.stageFilter
        }

        try {
          const data = await this.$store.dispatch('fundings/GET_FUNDINGS_BY_RG_DETAIL', gqlOptionsAndFilter)

          const { items, totalCount } = data.data.response

          this.totalCount = totalCount
          this.loadingShow = false
          this.dataIsLoaded = true
          this.$Progress.finish()

          return {
            data: items,
            totalCount
          }
        } catch (err) {
          errorHandler(this, err)
          console.error(err)
        }
      },
      getDisplayExpr (item) {
        return item.geschlecht_PopupExtension ? `${item.ansprechzeile} ${item.geschlecht_PopupExtension}` : `${item.ansprechzeile}`
      },
      openRowExpanding (e) {
        e.component.collapseAll(-1)
        if (!e.component.getSelectedRowKeys().length) { e.component.selectRowsByIndexes(0) }
      },
      publicRoundCellValue (rowData) {
        return rowData.privacyJoin === '1' ? 'Yes' : null
      },
      calculateSectors (rowData) {
        let listOfSectors = ''
        if (rowData.profile && rowData.profile.pROFILProfilJOINCategories.length) {
          listOfSectors = Array.prototype.map.call(rowData.profile.pROFILProfilJOINCategories, function (item) { if (item.pROFILCategory) { return item.pROFILCategory.kategorieDE } else { return '' } }).join(', ')
        }
        return listOfSectors
      },
      calculateInvestors (rowData) {
        let listOfInvestors = ''
        if (rowData.cRMFundingJOINProfilProfileV2s) {
          listOfInvestors = Array.prototype.map.call(rowData.cRMFundingJOINProfilProfileV2s, function (item) { if (item.profil_idId) { return item.profile.cRMAddress.company } else { return '' } }).join(', ')
        }
        return listOfInvestors
      },
      calculateAwards (rowData) {
        let listOfAwards = ''
        if (rowData.profile && rowData.profile.cRMAddress && rowData.profile.cRMAddress.cONTESTWinners) {
          listOfAwards = Array.prototype.map.call(rowData.profile.cRMAddress.cONTESTWinners, function (item) { if (item.cONTESTExecution && item.cONTESTExecution.cONTESTContest.category_idId === 1) { return item.cONTESTExecution.contest_release } else { return '' } }).join(', ')
        }
        return listOfAwards
      },
      calcTotal (rowData) {
        let total = 0
        if (rowData.profile) {
          if (rowData.profile.cRMFundingProfileV2s) {
            // statusId 2 = Closed, InvestmentStage 10 = Trade Sale, InvestmentType 9 = Loan, 15 = Revenue
            rowData.profile.cRMFundingProfileV2s.forEach(fund => {
              if (fund.statusId === 2 && fund.investmentsStage !== 10 &&
                ![9, 15].includes(fund.investmentsTypeId)) { (total += fund.moneyinCHF) }
            })
          }
        }
        return total
      },
      onExporting () {
        this.$store.commit('fundings/SET_EXPORT_TOTAL_COUNT', null)
        this.$store.commit('fundings/SET_EXPORT_TOTAL_COUNT', this.totalCount)
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Reportings')
        setTimeout(() => {
          exportDataGrid({
            component: this.dataGrid,
            worksheet: worksheet,
            autoFilterEnabled: true
          }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
              saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${moment(this.currentDate).format('DD.MM.YYYY')}-${this.stageFilter === 10 ? 'Reportings_Exits' : 'Reportings_IPOs'}.xlsx`)
            })
          })
          // this.dataGrid.cancel = true
        }, 1000)
      },
      async getBasicValues (storePath, storeValue) {
        if (storeValue.length === 0) {
          try {
            const { errors } = await this.$store.dispatch(storePath)
            if (errors) throw errors
          } catch (err) {
            this.hasError = true
            this.errorMessage = this.$t('formError.unknownwError')
            setTimeout(() => {
              this.hasError = false
            }, 5000)
            console.error(err)
          }
        }
      },
      openColumnChooser () {
        this.dataGrid.showColumnChooser()
      },
      checkAdmin (id) {
        return isAdmin(id)
      }
    },
    computed: {
      ...mapGetters({
        items: 'fundings/FUNDINGS',
        brancheTypes: 'basicValues/BRANCHE_TYPES',
        investmentTypes: 'basicValues/INVESTMENT_TYPES',
        investmentStages: 'basicValues/INVESTMENT_STAGES',
        profileStatus: 'basicValues/PROFILE_STATUS',
        cantonTypes: 'basicValues/CANTON_TYPES',
        salutationTypes: 'basicValues/SALUTATION_TYPES'
      }),
      ...mapState({
        currentUser: state => state.user.currentUser
      }),
      dataGrid: function () {
        return this.$refs[dataGridFundingsRG].instance
      }
    }
  }
</script>
<style lang="scss">
#gridContainerReporting {
    height: 60vh;
    margin-bottom: 0;
}

.btn-pdl {
  margin: 0 0 0 5px;
}

.dx-datagrid-export-button {
  &.disable-exporting {
    pointer-events: none;

    .dx-icon-xlsxfile {
      color: lightgray;
    }
  }
}

.reportings-list {
  margin-top: 3rem;
}

</style>
